import { Injectable } from '@angular/core';
import { ETNotification } from '@src/app/models/ETnotification';
import { SessionHelper } from './sessionHelper';

export interface NotificationStateSnapshot {
  all: ETNotification[];
  unread: ETNotification[];
  read: ETNotification[];
  unreadCount: number;
  latest: ETNotification | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {
  readonly unreadSentinel = '9999-01-01T00:00:00.000Z';

  private getCurrentUserId(): string {
    return SessionHelper.getUserSub();
  }

  private isCustomerSession(): boolean {
    return Boolean(SessionHelper.getCustomer());
  }

  buildState(notifications: ETNotification[] | null | undefined): NotificationStateSnapshot {
    const currentUserId = this.getCurrentUserId();
    const isCustomer = this.isCustomerSession();
    
    const all = (Array.isArray(notifications) ? notifications : [])
      .filter((notification) => {
        // For customer users, don't filter by personId
        // as customer notifications may have different personId values
        if (isCustomer) {
          return true;
        }
        // For regular org users, filter by personId
        return notification.personId === currentUserId;
      })
      .sort((left, right) => new Date(right.datecreated).getTime() - new Date(left.datecreated).getTime());

    const unread = all
      .filter((notification) => notification.dateread === this.unreadSentinel)
      .sort((left, right) => new Date(right.datecreated).getTime() - new Date(left.datecreated).getTime());

    const read = all
      .filter((notification) => notification.dateread !== this.unreadSentinel)
      .sort((left, right) => new Date(right.dateread || 0).getTime() - new Date(left.dateread || 0).getTime());

    return {
      all,
      unread,
      read,
      unreadCount: unread.length,
      latest: all[0],
    };
  }

  setSessionUnreadCount(notifications: ETNotification[] | null | undefined) {
    const snapshot = this.buildState(notifications);
    sessionStorage.setItem('numberOfNotifications', String(snapshot.unreadCount));
  }

  applyRead(notifications: ETNotification[] | null | undefined, notification: ETNotification, organizationId: string): ETNotification[] {
    return this.mergeNotification(notifications, {
      ...notification,
      organization: organizationId,
      dateread: new Date().toISOString(),
    });
  }

  applyUnread(notifications: ETNotification[] | null | undefined, notification: ETNotification, organizationId: string): ETNotification[] {
    return this.mergeNotification(notifications, {
      ...notification,
      organization: organizationId,
      dateread: this.unreadSentinel,
    });
  }

  mergeNotification(notifications: ETNotification[] | null | undefined, notification: ETNotification): ETNotification[] {
    const current = Array.isArray(notifications) ? [...notifications] : [];
    const index = current.findIndex((item) => item.notificationId === notification.notificationId);

    if (index === -1) {
      current.push(notification);
      return current;
    }

    current[index] = {
      ...current[index],
      ...notification,
    };

    return current;
  }
}